/* ═══════════════════════════════════════════════════════════════
   Module tag catalog helpers (migration 060, `module_tags`) — pure, no I/O.
   A tag lives as text on modules (`module_library.tags`); the catalog makes a
   tag exist before any module uses it. These helpers keep the two views in
   step. Matching is case-insensitive everywhere; the catalog's spelling wins.
   ═══════════════════════════════════════════════════════════════ */

import type { ModuleTag, SpecModule } from './spec-modules';

export interface TagRow {
  name: string;
  /** Modules (archived included) carrying the tag. */
  count: number;
  /** True when the catalog has a row for it. */
  inCatalog: boolean;
}

/** Catalog row operations a rename / merge implies. */
export interface CatalogOps {
  upsert: ModuleTag[];
  /** Catalog row ids to delete. */
  remove: string[];
}

const key = (s: string) => s.trim().toLowerCase();

/** Union of catalog tags and tags used on modules, de-duplicated
 *  case-insensitively (catalog spelling kept), sorted by name. */
export function buildTagRows(catalog: ModuleTag[], modules: SpecModule[]): TagRow[] {
  const rows = new Map<string, TagRow>();
  for (const t of catalog) {
    const k = key(t.name);
    if (k && !rows.has(k)) rows.set(k, { name: t.name.trim(), count: 0, inCatalog: true });
  }
  for (const m of modules) {
    const seenOnModule = new Set<string>();
    for (const raw of m.tags || []) {
      const k = key(raw);
      if (!k || seenOnModule.has(k)) continue;
      seenOnModule.add(k);
      const row = rows.get(k);
      if (row) row.count++;
      else rows.set(k, { name: raw.trim(), count: 1, inCatalog: false });
    }
  }
  return [...rows.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

/** Tags (trimmed, de-duplicated) that the catalog has no row for. */
export function tagsMissingFromCatalog(tags: string[], catalog: ModuleTag[]): string[] {
  const have = new Set(catalog.map((t) => key(t.name)));
  const out: string[] = [];
  for (const raw of tags) {
    const name = raw.trim();
    const k = key(name);
    if (!k || have.has(k)) continue;
    have.add(k);
    out.push(name);
  }
  return out;
}

/** Rename `from` to `to`. Renames the row; if `to` already has a row (a
 *  clash that merges the two) the `from` row is removed instead. */
export function applyRenameToCatalog(catalog: ModuleTag[], from: string, to: string): CatalogOps {
  const src = catalog.find((t) => key(t.name) === key(from));
  const dest = catalog.find((t) => key(t.name) === key(to));
  const name = to.trim();
  if (!src) return { upsert: [], remove: [] };
  if (dest && dest.id !== src.id) return { upsert: [], remove: [src.id] };
  return { upsert: [{ ...src, name }], remove: [] };
}

/** Merge `from` into `into`: drop the `from` row; `into` keeps (or gets,
 *  via the caller creating it) its own row. `upsert` is empty when `into`
 *  already has one. */
export function applyMergeToCatalog(
  catalog: ModuleTag[],
  from: string,
  into: string,
  makeRow: (name: string) => ModuleTag
): CatalogOps {
  const src = catalog.find((t) => key(t.name) === key(from));
  const dest = catalog.find((t) => key(t.name) === key(into));
  return {
    upsert: dest ? [] : [makeRow(into.trim())],
    remove: src && src.id !== dest?.id ? [src.id] : [],
  };
}

/** Picker suggestions: catalog names unioned with tags in use, most-used
 *  first then by name (the shape `ModuleTagsInput` takes). */
export function tagSuggestionsFrom(
  catalog: ModuleTag[],
  modules: SpecModule[]
): Array<{ tag: string; count: number }> {
  return buildTagRows(catalog, modules)
    .map((r) => ({ tag: r.name, count: r.count }))
    .sort(
      (a, b) => b.count - a.count || a.tag.localeCompare(b.tag, undefined, { sensitivity: 'base' })
    );
}
